import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_KEYS, PORTAL_META } from '../contexts/MarketContext';
import {
  DEFAULT_GOALS, DEFAULT_SEASON, GOAL_SETTINGS_PATH, normalizeGoal, seasonMonthsOf, validateGoals,
} from '../constants/goals';

const PALETTE = ['#1A4A8A', '#1A7A4A', '#7C3AED', '#E8A020', '#8DC63F', '#DC2626', '#0891B2', '#DB2777'];

const inputCls = 'w-full border border-[#E5E7EB] rounded-[8px] px-2.5 py-[7px] text-[13px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-pb';
const labelCls = 'block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.6px] mb-1';

function newKey() {
  return `goal_${Math.random().toString(36).slice(2, 8)}`;
}

// Only the fields an admin sets are stored; totals for monthly goals and the
// per-tour average are derived on read (normalizeGoal), so they can't drift.
function toStored(g) {
  const base = {
    key: g.key,
    label: String(g.label || '').trim(),
    color: g.color,
    portal: g.portal,
    groupTypes: (g.groupTypes || []).map((t) => t.trim()).filter(Boolean),
    pace: g.pace,
  };
  return g.pace === 'monthly'
    ? { ...base, toursPerMonth: Number(g.toursPerMonth) || 0, travellersPerMonth: Number(g.travellersPerMonth) || 0 }
    : { ...base, tours: Number(g.tours) || 0, travellers: Number(g.travellers) || 0 };
}

function GroupTypePicker({ value, onChange, options }) {
  const [draft, setDraft] = useState('');
  const listId = useMemo(() => `gt-${Math.random().toString(36).slice(2, 8)}`, []);
  function add() {
    const t = draft.trim();
    if (!t) return;
    if (!value.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setDraft('');
  }
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[24px]">
        {value.length === 0 && (
          <span className="text-[12px] text-[#6B7280] italic py-[3px]">Everything else in this portal</span>
        )}
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-[3px] rounded-full bg-blue-light text-pb">
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== t))}
              className="text-pb/60 hover:text-dr leading-none text-[14px]"
              aria-label={`Remove ${t}`}
            >×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          list={listId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a group type…"
          className={inputCls}
        />
        <datalist id={listId}>
          {options.filter((o) => !value.includes(o)).map((o) => <option key={o} value={o} />)}
        </datalist>
        <button type="button" onClick={add}
          className="px-3 text-[12px] font-bold text-pb border border-[#E5E7EB] rounded-[8px] hover:bg-surface">
          Add
        </button>
      </div>
    </div>
  );
}

/**
 * Admin editor for the season and its goals. Saves the whole set to
 * `goalSettings/current`; the Goals page and login splash pick it up live.
 */
export default function GoalsEditor({ raw, config, onClose }) {
  const { currentUser } = useAuth();
  const [season, setSeason] = useState(() => ({ ...DEFAULT_SEASON, ...(raw?.season || config.season) }));
  const [goals, setGoals] = useState(() => (raw?.goals || DEFAULT_GOALS).map((g) => ({ groupTypes: [], ...g })));
  const [groupTypeOptions, setGroupTypeOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getDocs(query(collection(db, 'groupTypes'), orderBy('order', 'asc')))
      .then((snap) => setGroupTypeOptions(snap.docs.map((d) => d.data().name).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const months = seasonMonthsOf(season);
  const normalized = goals.map((g) => normalizeGoal(g, months.length));
  const problems = [
    ...(months.length === 0 ? ['The season must end on or after the month it starts.'] : []),
    ...(!String(season.label || '').trim() ? ['The season needs a name.'] : []),
    ...validateGoals(normalized),
  ];

  function update(key, patch) {
    setGoals((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));
  }

  function setPace(g, pace) {
    if (pace === g.pace) return;
    // Carry the figures across so switching pace doesn't wipe the targets.
    const n = normalizeGoal(g, months.length || 1);
    const len = months.length || 1;
    update(g.key, pace === 'monthly'
      ? { pace, toursPerMonth: Math.round((n.tours / len) * 10) / 10, travellersPerMonth: Math.round(n.travellers / len) }
      : { pace, tours: n.tours, travellers: n.travellers });
  }

  function addGoal() {
    const used = new Set(goals.map((g) => g.color));
    setGoals((prev) => [...prev, {
      key: newKey(),
      label: '',
      color: PALETTE.find((c) => !used.has(c)) || PALETTE[0],
      portal: 'uk_to_spain',
      groupTypes: [],
      pace: 'season',
      tours: 0,
      travellers: 0,
    }]);
  }

  function move(index, dir) {
    setGoals((prev) => {
      const next = prev.slice();
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }

  async function save() {
    if (problems.length) return;
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(db, ...GOAL_SETTINGS_PATH), {
        season: {
          label: String(season.label).trim(),
          startMonth: season.startMonth,
          endMonth: season.endMonth,
          monthlyOverhead: Number(season.monthlyOverhead) || 0,
        },
        goals: goals.map(toStored),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || '',
      });
      onClose();
    } catch (err) {
      console.error('Failed to save goals', err);
      setError('Could not save the goals. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[150] flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(6,14,28,.55)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Edit season goals"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-[920px] my-6 bg-white rounded-[16px] shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-[16px] font-bold text-[#111827]">Edit season goals</h2>
            <p className="text-[12.5px] text-[#6B7280] mt-0.5">
              Changes apply straight away for everyone, on the Goals page and the sign-in splash.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-surface">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Season */}
          <section>
            <h3 className="text-[13px] font-bold text-[#111827] mb-3">Season</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input className={inputCls} value={season.label} placeholder="27/28"
                  onChange={(e) => setSeason((s) => ({ ...s, label: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>First month</label>
                <input type="month" className={inputCls} value={season.startMonth}
                  onChange={(e) => setSeason((s) => ({ ...s, startMonth: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Last month</label>
                <input type="month" className={inputCls} value={season.endMonth}
                  onChange={(e) => setSeason((s) => ({ ...s, endMonth: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Monthly overhead (£)</label>
                <input type="number" min="0" step="500" className={inputCls} value={season.monthlyOverhead}
                  onChange={(e) => setSeason((s) => ({ ...s, monthlyOverhead: e.target.value }))} />
              </div>
            </div>
            <p className="text-[12px] text-[#9CA3AF] mt-2">
              {months.length} month{months.length !== 1 ? 's' : ''}. Tours count in the month they depart, once the deposit is paid.
            </p>
          </section>

          {/* Goals */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[13px] font-bold text-[#111827]">Goals</h3>
              <button onClick={addGoal}
                className="px-3 py-[6px] text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition">
                + Add goal
              </button>
            </div>
            <p className="text-[12px] text-[#6B7280] mb-3">
              Each goal belongs to one portal and counts tours with the group types listed, plus any tour whose group type
              matches the goal's own name. Leave group types empty to make it that portal's catch-all, which counts every
              tour the other goals don't claim.
            </p>

            <div className="space-y-3">
              {goals.map((g, i) => {
                const n = normalized[i];
                return (
                  <div key={g.key} className="border border-[#E5E7EB] rounded-[12px] p-4">
                    <div className="flex items-start gap-3">
                      <input type="color" value={g.color} aria-label="Colour"
                        onChange={(e) => update(g.key, { color: e.target.value })}
                        className="w-9 h-9 rounded-[8px] border border-[#E5E7EB] cursor-pointer flex-shrink-0 p-0.5" />
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Name</label>
                          <input className={inputCls} value={g.label} placeholder="e.g. Long Haul"
                            onChange={(e) => update(g.key, { label: e.target.value })} />
                        </div>
                        <div>
                          <label className={labelCls}>Portal</label>
                          <select className={inputCls} value={g.portal}
                            onChange={(e) => update(g.key, { portal: e.target.value })}>
                            {PORTAL_KEYS.map((p) => <option key={p} value={p}>{PORTAL_META[p].label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Target type</label>
                          <select className={inputCls} value={g.pace} onChange={(e) => setPace(g, e.target.value)}>
                            <option value="monthly">Per month (paced)</option>
                            <option value="season">Season total</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"
                          className="w-7 h-6 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-[6px] hover:bg-surface disabled:opacity-30">↑</button>
                        <button onClick={() => move(i, 1)} disabled={i === goals.length - 1} aria-label="Move down"
                          className="w-7 h-6 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-[6px] hover:bg-surface disabled:opacity-30">↓</button>
                      </div>
                    </div>

                    <div className="mt-3 sm:pl-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {g.pace === 'monthly' ? (
                        <>
                          <div>
                            <label className={labelCls}>Tours per month</label>
                            <input type="number" min="0" step="0.5" className={inputCls} value={g.toursPerMonth ?? ''}
                              onChange={(e) => update(g.key, { toursPerMonth: e.target.value })} />
                          </div>
                          <div>
                            <label className={labelCls}>Travellers per month</label>
                            <input type="number" min="0" className={inputCls} value={g.travellersPerMonth ?? ''}
                              onChange={(e) => update(g.key, { travellersPerMonth: e.target.value })} />
                          </div>
                          <div className="text-[12px] text-[#6B7280] self-end pb-2">
                            Season: <strong className="text-[#111827]">{n.tours}</strong> tours ·{' '}
                            <strong className="text-[#111827]">{n.travellers}</strong> travellers
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className={labelCls}>Tours (season)</label>
                            <input type="number" min="0" className={inputCls} value={g.tours ?? ''}
                              onChange={(e) => update(g.key, { tours: e.target.value })} />
                          </div>
                          <div>
                            <label className={labelCls}>Travellers (season)</label>
                            <input type="number" min="0" className={inputCls} value={g.travellers ?? ''}
                              onChange={(e) => update(g.key, { travellers: e.target.value })} />
                          </div>
                          <div className="text-[12px] text-[#6B7280] self-end pb-2">
                            ≈ <strong className="text-[#111827]">{n.travellersPerTour}</strong> travellers per tour
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-3 sm:pl-12 flex items-end gap-3">
                      <div className="flex-1">
                        <label className={labelCls}>Group types that count</label>
                        <GroupTypePicker
                          value={g.groupTypes || []}
                          onChange={(groupTypes) => update(g.key, { groupTypes })}
                          options={groupTypeOptions}
                        />
                      </div>
                      <button
                        onClick={() => setGoals((prev) => prev.filter((x) => x.key !== g.key))}
                        className="text-[12px] font-semibold text-[#9CA3AF] hover:text-dr pb-2"
                      >
                        Remove goal
                      </button>
                    </div>
                  </div>
                );
              })}
              {goals.length === 0 && (
                <p className="text-[13px] text-[#9CA3AF] text-center py-6 border border-dashed border-[#E5E7EB] rounded-[12px]">
                  No goals. Add one to start tracking.
                </p>
              )}
            </div>
          </section>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          {problems.length > 0 && (
            <ul className="mb-3 space-y-1">
              {problems.map((p) => <li key={p} className="text-[12.5px] text-dr">• {p}</li>)}
            </ul>
          )}
          {error && <p className="mb-3 text-[12.5px] text-dr">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose}
              className="px-4 py-2 text-[13px] font-bold text-[#374151] border border-[#E5E7EB] bg-white rounded-[9px] hover:bg-surface">
              Cancel
            </button>
            <button onClick={save} disabled={saving || problems.length > 0}
              className="px-5 py-2 text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-50">
              {saving ? 'Saving…' : 'Save goals'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
